import React, { useState } from 'react';
import './Viaje.css';
import DateInput from './DateInput';
import StyledInput from './StyledInput';
import StyledDateInput from './StyledDateInput';
import StyledTimeInput from './StyledTimeInput';
import TransportTypeSelector from './TransportTypeSelector';
import CountrySelector, { COUNTRIES } from './CountrySelector';

const Viaje = ({ viaje, index, onChange, onDelete, monthIndex, year }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const handleFieldChange = (field, value) => {
    const updatedViaje = { ...viaje, [field]: value };
    onChange(index, updatedViaje);
  };

  const handleTransporteChange = (transporteIndex, field, value) => {
    const transportes = [...(viaje.transportes || viaje.vuelos || [])];
    transportes[transporteIndex] = { ...transportes[transporteIndex], [field]: value };
    handleFieldChange('transportes', transportes);
  };

  const addTransporte = () => {
    // Inicializar con el día 01 del mes actual
    const firstDayOfMonth = new Date(year, monthIndex, 1);
    const fechaDefault = firstDayOfMonth.toISOString().split('T')[0];
    
    const transportes = [...(viaje.transportes || viaje.vuelos || []), {
      tipo: 'vuelo', // Por defecto vuelo
      fecha: fechaDefault,
      compania: '', // Genérico para aerolínea/empresa de tren/bus
      numero: '', // Genérico para número de vuelo/tren/bus
      numeroReserva: '',
      origen: '', // Genérico para aeropuerto/estación
      destino: '', // Genérico para aeropuerto/estación
      horaSalida: '',
      horaLlegada: ''
    }];
    handleFieldChange('transportes', transportes);
  };

  const removeTransporte = (transporteIndex) => {
    const transportes = (viaje.transportes || viaje.vuelos || []).filter((_, i) => i !== transporteIndex);
    handleFieldChange('transportes', transportes);
  };

  // Migrar vuelos antiguos a transportes si es necesario
  const transportes = viaje.transportes || (viaje.vuelos ? viaje.vuelos.map(v => ({
    tipo: 'vuelo',
    fecha: v.fecha,
    compania: v.aerolinea || '',
    numero: v.numeroVuelo || '',
    numeroReserva: v.numeroReserva || '',
    origen: v.aeropuertoSalida || '',
    destino: v.aeropuertoLlegada || '',
    horaSalida: v.horaSalida || '',
    horaLlegada: v.horaLlegada || ''
  })) : []);

  const handleAirbnbChange = (airbnbIndex, field, value) => {
    const airbnbs = [...(viaje.airbnbs || [])];
    airbnbs[airbnbIndex] = { ...airbnbs[airbnbIndex], [field]: value };
    handleFieldChange('airbnbs', airbnbs);
  };

  const addAirbnb = () => {
    // Inicializar con el día 01 del mes actual
    const firstDayOfMonth = new Date(year, monthIndex, 1);
    const fechaDefault = firstDayOfMonth.toISOString().split('T')[0];
    
    const airbnbs = [...(viaje.airbnbs || []), {
      nombre: '',
      checkIn: fechaDefault,
      checkOut: fechaDefault,
      direccion: '',
      numeroReserva: ''
    }];
    handleFieldChange('airbnbs', airbnbs);
  };

  const removeAirbnb = (airbnbIndex) => {
    const airbnbs = viaje.airbnbs.filter((_, i) => i !== airbnbIndex);
    handleFieldChange('airbnbs', airbnbs);
  };

  return (
    <div className="viaje-card">
      <div className="viaje-header">
        <button
          type="button"
          className="expand-toggle-btn"
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? "Contraer" : "Expandir"}
        >
          {isExpanded ? '▼' : '▶'}
        </button>
        <h3 className="viaje-title">Viaje #{index + 1}</h3>
        {(viaje.nombre || viaje.pais) && (
          <span className="viaje-destino-preview">
            {viaje.pais && COUNTRIES.find(c => c.code === viaje.pais)?.flag} {viaje.nombre || COUNTRIES.find(c => c.code === viaje.pais)?.name}
          </span>
        )}
        <button 
          type="button" 
          className="delete-viaje-btn"
          onClick={() => onDelete(index)}
          title="Eliminar viaje"
        >
          ×
        </button>
      </div>

      {isExpanded && (
        <div className="viaje-content">

      <div className="form-row">
        <StyledInput
          value={viaje.nombre || ''}
          onChange={(value) => handleFieldChange('nombre', value)}
          label="Nombre (opcional)"
          placeholder="Ej: Viaje a París"
          variant="destination"
        />
        <CountrySelector
          value={viaje.pais || ''}
          onChange={(value) => handleFieldChange('pais', value)}
        />
      </div>

      <div className="form-row date-row">
        <DateInput
          value={viaje.fechaInicio}
          onChange={(value) => handleFieldChange('fechaInicio', value)}
          label="Fecha Inicio"
          monthIndex={monthIndex}
          year={year}
          type="start"
        />
        <DateInput
          value={viaje.fechaFin}
          onChange={(value) => handleFieldChange('fechaFin', value)}
          label="Fecha Fin"
          monthIndex={monthIndex}
          year={year}
          type="end"
        />
      </div>

      {/* Sección de Transporte */}
      <div className="viaje-section">
        <div className="section-header">
          <h4 className="section-subtitle">
            <span className="icon">🚌</span>
            Transporte
          </h4>
          <button 
            type="button" 
            className="add-item-btn"
            onClick={addTransporte}
          >
            ➕ Agregar Transporte
          </button>
        </div>

        {transportes.map((transporte, transporteIndex) => {
          const getTypeIcon = (tipo) => {
            switch(tipo) {
              case 'tren': return '🚂';
              case 'bus': return '🚌';
              default: return '✈️';
            }
          };

          const getTypeLabel = (tipo) => {
            switch(tipo) {
              case 'tren': return 'Tren';
              case 'bus': return 'Bus';
              default: return 'Vuelo';
            }
          };

          const getCompaniaLabel = (tipo) => {
            switch(tipo) {
              case 'tren': return 'Compañía de Tren';
              case 'bus': return 'Compañía de Bus';
              default: return 'Aerolínea';
            }
          };

          const getNumeroLabel = (tipo) => {
            switch(tipo) {
              case 'tren': return 'Número de Tren';
              case 'bus': return 'Número de Bus';
              default: return 'Número de Vuelo';
            }
          };

          const getOrigenLabel = (tipo) => {
            switch(tipo) {
              case 'tren': return 'Estación Salida';
              case 'bus': return 'Terminal Salida';
              default: return 'Aeropuerto Salida';
            }
          };

          const getDestinoLabel = (tipo) => {
            switch(tipo) {
              case 'tren': return 'Estación Llegada';
              case 'bus': return 'Terminal Llegada';
              default: return 'Aeropuerto Llegada';
            }
          };

          const tipo = transporte.tipo || 'vuelo';

          return (
            <div key={transporteIndex} className={`item-card transporte-card transporte-${tipo}`}>
              <div className="item-header">
                <span className={`item-badge transporte-badge transporte-badge-${tipo}`}>
                  {getTypeIcon(tipo)} {getTypeLabel(tipo)} {transporteIndex + 1}
                </span>
                <button 
                  type="button"
                  className="remove-item-btn"
                  onClick={() => removeTransporte(transporteIndex)}
                >
                  ×
                </button>
              </div>

              <div className="form-row">
                <div className="transport-type-selector-wrapper">
                  <label className="form-group-label">Tipo de Transporte</label>
                  <TransportTypeSelector
                    value={tipo}
                    onChange={(newTipo) => handleTransporteChange(transporteIndex, 'tipo', newTipo)}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <StyledDateInput
                  value={transporte.fecha}
                  onChange={(value) => handleTransporteChange(transporteIndex, 'fecha', value)}
                  label="Fecha"
                  variant="flight"
                />
                <StyledInput
                  value={transporte.compania}
                  onChange={(value) => handleTransporteChange(transporteIndex, 'compania', value)}
                  label={getCompaniaLabel(tipo)}
                  placeholder={`Ej: ${tipo === 'tren' ? 'Renfe, SNCF' : tipo === 'bus' ? 'Flixbus, ALSA' : 'Emirates, LATAM'}`}
                  variant="flight"
                  icon={getTypeIcon(tipo)}
                />
                <StyledInput
                  value={transporte.numero}
                  onChange={(value) => handleTransporteChange(transporteIndex, 'numero', value)}
                  label={getNumeroLabel(tipo)}
                  placeholder={`Ej: ${tipo === 'tren' ? 'AVE123' : tipo === 'bus' ? 'FB456' : 'EK201'}`}
                  variant="flight"
                />
              </div>

              <div className="form-row">
                <StyledInput
                  value={transporte.origen}
                  onChange={(value) => handleTransporteChange(transporteIndex, 'origen', value)}
                  label={getOrigenLabel(tipo)}
                  placeholder={`Ej: ${tipo === 'tren' ? 'Estación Central' : tipo === 'bus' ? 'Terminal Norte' : 'SCL'}`}
                  variant="flight"
                  icon="🛫"
                />
                <StyledInput
                  value={transporte.destino}
                  onChange={(value) => handleTransporteChange(transporteIndex, 'destino', value)}
                  label={getDestinoLabel(tipo)}
                  placeholder={`Ej: ${tipo === 'tren' ? 'Estación Sur' : tipo === 'bus' ? 'Terminal Sur' : 'CDG'}`}
                  variant="flight"
                  icon="🛬"
                />
              </div>

              <div className="form-row">
                <StyledTimeInput
                  value={transporte.horaSalida}
                  onChange={(value) => handleTransporteChange(transporteIndex, 'horaSalida', value)}
                  label="Hora Salida"
                />
                <StyledTimeInput
                  value={transporte.horaLlegada}
                  onChange={(value) => handleTransporteChange(transporteIndex, 'horaLlegada', value)}
                  label="Hora Llegada"
                />
                <StyledInput
                  value={transporte.numeroReserva}
                  onChange={(value) => handleTransporteChange(transporteIndex, 'numeroReserva', value)}
                  label="Número de Reserva"
                  placeholder="Ej: ABC123"
                  variant="flight"
                  icon="🎫"
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Sección de Airbnb/Alojamientos */}
      <div className="viaje-section">
        <div className="section-header">
          <h4 className="section-subtitle">
            <span className="icon">🏠</span>
            Alojamientos
          </h4>
          <button 
            type="button" 
            className="add-item-btn"
            onClick={addAirbnb}
          >
            + Agregar Alojamiento
          </button>
        </div>

        {(viaje.airbnbs || []).map((airbnb, airbnbIndex) => (
          <div key={airbnbIndex} className="item-card airbnb-card">
            <div className="item-header">
              <span className="item-badge airbnb-badge">Alojamiento {airbnbIndex + 1}</span>
              <button 
                type="button"
                className="remove-item-btn"
                onClick={() => removeAirbnb(airbnbIndex)}
              >
                ×
              </button>
            </div>
            
            <div className="form-row">
              <StyledInput
                value={airbnb.nombre}
                onChange={(value) => handleAirbnbChange(airbnbIndex, 'nombre', value)}
                label="Nombre"
                placeholder="Ej: Apartamento Centro París"
                variant="accommodation"
              />
              <StyledInput
                value={airbnb.numeroReserva}
                onChange={(value) => handleAirbnbChange(airbnbIndex, 'numeroReserva', value)}
                label="Número de Reserva"
                placeholder="Ej: RES123456"
                variant="accommodation"
                icon="🔑"
              />
            </div>

            <div className="form-row">
              <StyledDateInput
                value={airbnb.checkIn}
                onChange={(value) => handleAirbnbChange(airbnbIndex, 'checkIn', value)}
                label="Check-in"
                variant="accommodation"
              />
              <StyledDateInput
                value={airbnb.checkOut}
                onChange={(value) => handleAirbnbChange(airbnbIndex, 'checkOut', value)}
                label="Check-out"
                variant="accommodation"
              />
            </div>

            <div className="form-row">
              <StyledInput
                value={airbnb.direccion}
                onChange={(value) => handleAirbnbChange(airbnbIndex, 'direccion', value)}
                label="Dirección"
                placeholder="Ej: 123 Rue de la Paix, París"
                variant="accommodation"
                icon="📍"
              />
            </div>
          </div>
        ))}
      </div>
        </div>
      )}
    </div>
  );
};

export default Viaje;

